<div class="flex flex-col lg:flex-row gap-4">
  <!-- Left Content -->
  <div class="flex h-fit flex-col w-full lg:w-[50%] p-6 lg:p-11 card">
    <form (submit)="handleSave($event)" class="flex flex-col h-full">
      <div class="flex flex-col gap-3 lg:gap-1.5 w-full flex-grow">
        <p class="text-2xl lg:text-4xl font-bold mb-4">Create Problem</p>

        <!-- Problem Name -->
        <div class="flex-col w-full">
          <div class="flex">
            <legend class="fieldset-legend">
              Problem Name<sup class="text-red-500">*</sup>
            </legend>
          </div>
          <input
            name="problemName"
            type="text"
            [value]=""
            class="w-full input placeholder-gray-300 rounded-lg"
            placeholder="Enter Problem Name"
            (keydown.enter)="$event.preventDefault()"
            (change)="validateProblemName($event)"
            required />
        </div>

        <!-- Problem Difficulty / Duration -->
        <div class="flex flex-col sm:flex-row gap-4">
          <!-- Problem Difficulty -->
          <div class="flex-col w-full sm:w-[65%]">
            <div class="flex">
              <legend class="fieldset-legend">
                Pick Difficulty of Problem<sup class="text-red-500">*</sup>
              </legend>
            </div>
            <app-select
              data-testid="difficulty-selector"
              [id]="'difficulty-selector'"
              [name]="'difficulty-selector'"
              [items]="selectDifficultyItems()"
              [(selectedItem)]="selectedDifficulty" />
          </div>

          <!-- Problem Duration -->
          <div class="flex-col w-full sm:w-[35%]">
            <div class="flex">
              <legend class="fieldset-legend">
                Duration<sup class="text-red-500">*</sup>
              </legend>
            </div>
            <label class="input rounded-lg w-full">
              <input
                name="problemDuration"
                type="number"
                value="1"
                min="1"
                (keydown.enter)="$event.preventDefault()"
                (change)="validateProblemDuration($event)"
                required
                id="problem-duration" />
              <span class="label">minutes</span>
            </label>
          </div>
        </div>

        <!-- Problem Description -->
        <div class="flex-col w-full">
          <div class="flex">
            <legend class="fieldset-legend">
              Description<sup class="text-red-500">*</sup>
            </legend>
          </div>
          <div class="w-full" data-testid="description-editor">
            <div #quillEditorContainer></div>
          </div>
        </div>
      </div>

      <!-- Button Container -->
      <div class="flex flex-col mt-5 gap-2">
        <div class="flex w-full flex-row gap-3">
          <div class="w-full">
            <app-button
              type="button"
              label="Cancel"
              (clicked)="goToProblems()" />
          </div>
          <div class="w-full">
            <app-button
              color="primary"
              type="submit"
              label="Create"
              [disabled]="!isFormValid()" />
          </div>
        </div>
        <div class="w-full">
          <p class="text-center text-gray-500">
            Make sure all test cases are configured, description is provided,
            and verification is completed before creating the problem.
          </p>
        </div>
      </div>
    </form>
  </div>

  <!-- Right Content -->
  <div class="flex flex-col lg:w-[50%] w-full h-full gap-4">
    <!-- Tab Selector with Side Animation -->
    <div
      class="flex flex-row justify-center items-center card overflow-hidden h-15 relative p-2">
      <div class="flex justify-center w-full h-full relative">
        <!-- Sliding Background -->
        <div
          class="absolute bg-primary h-full w-1/3 rounded-lg transition-all duration-300"
          [style.left]="selectedTab() === 'initial-code' ? '0%' : selectedTab() === 'test-case' ? '33.33%' : '66.66%'"></div>
        <!-- Buttons -->
        <button
          id="initial-code-button"
          (click)="handleSelectTab('initial-code')"
          class="w-1/3 text-base xl:text-xl font-semibold m-2 cursor-pointer relative z-1 transition-colors duration-300"
          [class.text-white]="selectedTab() === 'initial-code'">
          Initial code
        </button>
        <button
          id="test-case-button"
          (click)="handleSelectTab('test-case')"
          class="w-1/3 text-base xl:text-xl font-semibold m-2 cursor-pointer relative z-1 transition-colors duration-300"
          [class.text-white]="selectedTab() === 'test-case'">
          Test Case
        </button>
        <button
          id="verification-code-button"
          (click)="handleSelectTab('verification-code')"
          class="w-1/3 text-base xl:text-xl font-semibold m-2 cursor-pointer relative z-1 transition-colors duration-300"
          [class.text-white]="selectedTab() === 'verification-code'">
          Verification code
        </button>
      </div>
    </div>

    <!-- Content Area -->
    <div class="flex-1 card overflow-hidden">
      @if (selectedTab() === 'initial-code' || selectedTab() ===
      'verification-code') {
      <div
        class="h-[50vh] lg:h-[60vh] bg-white shadow-lg overflow-hidden flex flex-col flex-shrink-0"
        [class.rounded-lg]="selectedTab() === 'initial-code'">
        <div
          class="w-full flex flex-col sm:flex-row items-start sm:items-center px-7 py-6 flex-shrink-0 gap-2 sm:gap-0">
          <p class="w-full font-semibold text-lg sm:text-xl lg:text-2xl">
            Write your code here..
          </p>
          <div class="flex w-full sm:justify-end">
            <app-select
              [id]="'language-selector'"
              [name]="'language-selector'"
              [items]="selectLanguageItems()"
              [(selectedItem)]="selectedLanguage" />
          </div>
        </div>
        <div
          class="flex-1 min-h-0"
          id="code-editor-container-{{selectedTab()}}">
          <div #codeEditorOutlet></div>
        </div>
      </div>
      } @else {
      <div
        class="w-full p-2 sm:p-4 gap-2.5 flex flex-col"
        data-testid="test-case-container">
        @for (testCase of testCases(); track testCase.id; let i = $index) {
        <app-test-case-item
          [index]="i + 1"
          [data]="testCase"
          (deleteButtonClicked)="removeTestCase(testCase.id)"
          (dataChanged)="updateTestCase(testCase.id, $event)" />
        }
        <button
          data-testid="add-test-case-button"
          (click)="addTestCase()"
          class="flex justify-center items-center cursor-pointer border-1 border-dashed border-primary w-full h-14 rounded-lg">
          <svg
            width="19"
            height="18"
            viewBox="0 0 19 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M17 9C17 9.41421 16.6642 9.75 16.25 9.75H10.25V15.75C10.25 16.1642 9.91421 16.5 9.5 16.5C9.08579 16.5 8.75 16.1642 8.75 15.75V9.75H2.75C2.33579 9.75 2 9.41421 2 9C2 8.58579 2.33579 8.25 2.75 8.25H8.75V2.25C8.75 1.83579 9.08579 1.5 9.5 1.5C9.91421 1.5 10.25 1.83579 10.25 2.25V8.25H16.25C16.6642 8.25 17 8.58579 17 9Z"
              fill="#0079bf" />
          </svg>
        </button>
        <div class="text-sm text-gray-500">
          * Provide function parameters separated by commas: param1, param2, ...
          <br />
          * For array parameters, use square brackets: [1,2] <br />
          * Example: [1,2], "abc", true, [3,4,5]
        </div>
      </div>
      }
    </div>

    @if(selectedTab() === 'verification-code') {
    <app-test-case-card
      (submitClick)="handleRunVerify()"
      [testCaseDetails]="testCaseDetail()"
      [isForVerify]="true"
      [running]="running()" />
    <!-- show error -->
    @if (errorMessage()) {
    <div
      class="mt-4 p-3 sm:p-4 bg-error-subtle border border-error text-error rounded-xl">
      <div class="flex items-center">
        <svg
          class="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0"
          fill="currentColor"
          viewBox="0 0 20 20">
          <path
            fill-rule="evenodd"
            d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
            clip-rule="evenodd"></path>
        </svg>
        <span class="font-medium">{{ errorMessage() }}</span>
      </div>
    </div>
    } }
  </div>
</div>
